import React, { FC } from 'react'

import styled from 'styled-components'

import { WithClassName } from 'common/types'
import { ProfileAge } from 'components/presentational/ProfileAge/ProfileAge'
import {
  ProfileNameWrapper,
  Name,
  TruncateText,
  StatusWrapper,
  ProfileNamePhotoline,
} from 'components/presentational/ProfileName/ProfileName.styled'
import { ProfileOnline } from 'components/presentational/ProfileOnline/ProfileOnline'
import { ProfileVerification } from 'components/presentational/ProfileVerification/ProfileVerification'
import { GQLGender } from 'generated-graphql/graphql'

import { ProfileMute } from '../ProfileMute/ProfileMute'

/**
 * Случай
 * 1. Фотолинейка, там особое поведение
 * 2. Когда имя состоит из нескольких частей (поиск, анкета).
 * 3. И все остальные когда мы можем все вытянуть в одну строку и обрезать если место нету.
 * @returns
 */

export const ProfileName: FC<
  {
    fullName: string
    age?: number | null
    online?: boolean
    statusVerification?: boolean
    gender?: GQLGender
    isMute?: boolean
    premium?: boolean
    nameIsTwoRow?: boolean
    nameInCenter?: boolean
    photolineTooltip?: boolean
    iconUid?: string
  } & WithClassName
> = ({
  fullName,
  age,
  online,
  statusVerification,
  gender,
  premium,
  isMute,
  nameIsTwoRow,
  nameInCenter,
  photolineTooltip,
  iconUid,
  className,
}) => {
  const fullNameSplit = fullName.split(' ')

  const ageStatus = Boolean(age) && <ProfileAge age={age!} />
  const onlineStatus = Boolean(online) && <ProfileOnline />
  const verificationStatus = (
    <ProfileVerification
      statusVerification={statusVerification}
      gender={gender}
      name={fullName}
      iconUid={iconUid!}
    />
  )

  const mute = Boolean(isMute) && <ProfileMute />

  /**
   * withoutMarginInChild для https://youtrack.mamba.ru/issue/M-3502
   * И только для хитлиста нужно убрать margin у icon если имени нет.
   * Имени не будет для хитов гостей.
   */
  const statusBlock = (
    <StatusWrapper
      $withBackground={premium}
      $withoutMarginInChild={fullName.length === 0}
    >
      {onlineStatus}
      {verificationStatus}
      {mute}
    </StatusWrapper>
  )

  if (photolineTooltip) {
    return (
      <ProfileNamePhotoline className={className}>
        {fullName}
        {ageStatus}
        <span style={{ margin: '0 1px' }} />
        {onlineStatus}
        {verificationStatus}
      </ProfileNamePhotoline>
    )
  }

  if (
    Array.isArray(fullNameSplit) &&
    fullNameSplit.length > 1 &&
    nameIsTwoRow
  ) {
    const [first, ...rest] = fullNameSplit
    return (
      <ProfileNameWrapper className={className}>
        <Name $nameInCenter={nameInCenter}>
          <TruncateText data-name="item-title-first-name">{first}</TruncateText>
        </Name>
        <Name $nameInCenter={nameInCenter}>
          <TruncateText data-name="item-title-last-name">
            {rest.join(' ')}
          </TruncateText>
          {ageStatus}
          {statusBlock}
        </Name>
      </ProfileNameWrapper>
    )
  }

  return (
    <ProfileNameWrapper className={className}>
      <Name $nameInCenter={nameInCenter}>
        <TruncateText data-name="item-title-name">{fullName}</TruncateText>
        {ageStatus}
        {statusBlock}
      </Name>
    </ProfileNameWrapper>
  )
}

export const PresentVipName = styled(ProfileName)`
  display: inline-block;
  font-size: 12px;
  white-space: nowrap;
  margin: 0 0 4px;
  font-weight: bold;
  color: var(--static-black);
`
